import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, MessageSquare, Pencil, Trash2, ImageOff, Loader2, Handshake, Coins } from "lucide-react";
import { Avatar } from "@/components/community/ChatList";
import { GlassButton } from "@/components/GlassBits";
import { money, StatusChip } from "@/components/market/ProductCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** Ficha del producto con galería de hasta 20 fotos y botón de compra. */
export const ProductDetail = ({ product, onClose, onEdit, onDeleted, onChanged }) => {
  const [i, setI] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const photos = product.media || [];

  const buy = async () => {
    setBusy("buy");
    try {
      await userApi.post(`/market/products/${product.product_id}/contact`);
      toast.success("Le escribimos al vendedor. Sigue el trato en tus chats.");
      nav("/comunidad");
    } catch (e) { toast.error(apiError(e, "No se pudo contactar al vendedor")); }
    finally { setBusy(false); }
  };

  const buyCredits = async () => {
    const total = product.buyer_total_credits;
    if (!window.confirm(
      `Vas a pagar ${total} créditos (${product.price_credits} del producto + ${product.fee_pct}% de comisión).\n\n` +
      `Tus créditos quedan retenidos hasta que el administrador apruebe el trato. ` +
      `Si lo rechaza, se te devuelven completos.`)) return;
    setBusy("credits");
    try {
      const { data } = await userApi.post(`/market/products/${product.product_id}/buy-credits`);
      toast.success(`Pagaste ${data.buyer_paid} créditos. El administrador revisará el trato.`);
      onChanged?.();
    } catch (e) { toast.error(apiError(e, "No se pudo pagar con créditos")); }
    finally { setBusy(false); }
  };

  const adminDeal = async () => {
    setBusy("deal");
    try {
      const { data } = await userApi.post(`/market/products/${product.product_id}/admin-deal`);
      toast.success(data.already
        ? "Ya avisamos al administrador por este producto."
        : "Listo: el administrador recibió la alerta y te va a ayudar con el trato.");
    } catch (e) { toast.error(apiError(e, "No se pudo avisar al administrador")); }
    finally { setBusy(false); }
  };

  const remove = async () => {    if (!window.confirm("¿Quitar este producto del mercado?")) return;
    try {
      await userApi.delete(`/market/products/${product.product_id}`);
      toast.success("Producto retirado");
      onDeleted?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(3,0,0,0.78)" }} data-testid="product-detail">
      <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="relative bg-black/50 aspect-[16/10]">
          {photos.length ? (
            <>
              <img src={`${BACKEND}${photos[i].url}`} alt={product.title}
                data-testid="product-detail-photo" onLoad={() => setLoaded(true)}
                className={`w-full h-full object-contain transition-opacity duration-300 ${
                  loaded ? "opacity-100" : "opacity-0"}`} />
              {!loaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-7 h-7 animate-spin text-red-400/80" />
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/25">
              <ImageOff className="w-9 h-9" />
            </div>
          )}
          <button data-testid="product-detail-close" onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center border border-white/20"
            style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(12px)" }}>
            <X className="w-4 h-4 text-white" />
          </button>
          {photos.length > 1 && (
            <>
              <button data-testid="product-prev-photo"
                onClick={() => { setLoaded(false); setI((v) => (v - 1 + photos.length) % photos.length); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center border border-white/20"
                style={{ background: "rgba(0,0,0,0.55)" }}>
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
              <button data-testid="product-next-photo"
                onClick={() => { setLoaded(false); setI((v) => (v + 1) % photos.length); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center border border-white/20"
                style={{ background: "rgba(0,0,0,0.55)" }}>
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[11px] text-white/80 px-2.5 py-0.5 rounded-full"
                style={{ background: "rgba(0,0,0,0.6)" }} data-testid="product-photo-counter">
                {i + 1} / {photos.length}
              </div>
            </>
          )}
        </div>

        {photos.length > 1 && (
          <div className="flex gap-2 px-5 pt-4 overflow-x-auto">
            {photos.map((m, idx) => (
              <button key={m.media_id} onClick={() => { setLoaded(false); setI(idx); }}
                data-testid={`product-thumb-${idx}`}
                className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border ${
                  idx === i ? "border-red-500/70" : "border-white/12"}`}>
                <img src={`${BACKEND}${m.url}`} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-white font-heading font-bold text-xl leading-tight break-words"
                data-testid="product-detail-title">{product.title}</h2>
              <div className="text-red-300 font-bold text-lg mt-1">
                {money(product.price, product.currency)}
              </div>
              {product.accepts_credits && (
                <div className="text-[11px] text-amber-300/80 mt-0.5">
                  o {product.price_credits} créditos (+{product.fee_pct}% de comisión)
                </div>
              )}
            </div>
            <StatusChip status={product.status} />
          </div>

          {product.description && (
            <p className="text-sm text-white/75 whitespace-pre-wrap mt-4 leading-relaxed"
              data-testid="product-detail-desc">{product.description}</p>
          )}

          <div className="flex items-center gap-3 mt-5 pt-4 border-t border-white/10">
            <Avatar person={product.seller} size={38} />
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{product.seller?.name || "Miembro"}</div>
              <div className="text-[11px] text-white/45">
                Publicado {(product.created_at || "").slice(0, 10)}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-5">
            {product.mine ? (
              <div className="flex gap-2">
                <GlassButton tone="red" icon={Pencil} data-testid="product-edit-btn"
                  onClick={() => onEdit?.(product)}>Editar</GlassButton>
                <GlassButton tone="neutral" icon={Trash2} data-testid="product-delete-btn"
                  onClick={remove}>Quitar</GlassButton>
              </div>
            ) : (
              <>
                {product.accepts_credits && product.status !== "vendido" && (
                  <GlassButton tone="red" icon={Coins} loading={busy === "credits"}
                    data-testid="product-buy-credits-btn" onClick={buyCredits}>
                    Comprar con créditos · {product.buyer_total_credits} cr
                  </GlassButton>
                )}
                <GlassButton tone="neutral" icon={MessageSquare} loading={busy === "buy"}
                  data-testid="product-buy-btn" onClick={buy}
                  disabled={product.status === "vendido"}>
                  {product.status === "vendido" ? "Ya se vendió" : "Hablar con el vendedor"}
                </GlassButton>
              </>
            )}
            {!product.mine && (
              <>
                <GlassButton tone="neutral" icon={Handshake} loading={busy === "deal"}
                  data-testid="product-admin-deal-btn" onClick={adminDeal}>
                  Trato con admin
                </GlassButton>
                <p className="text-[11px] text-white/45 text-center">
                  Pulsa «Trato con admin» y le llega una alerta al administrador para que medie el trato.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
